import React, { useState } from 'react';
import { Box, Typography, Button, Dialog, DialogTitle, DialogContent, DialogActions, TextField } from '@mui/material';
import BusinessIcon from '@mui/icons-material/Business';
import SchoolIcon from '@mui/icons-material/School';
import HandshakeIcon from '@mui/icons-material/Handshake';

const PricingCards = ({ onStartFree, showButtons = true, hasSubscription = false, isAuthenticated = false }) => {
  const [orgAccessModalOpen, setOrgAccessModalOpen] = useState(false);
  const [orgRequestData, setOrgRequestData] = useState({ name: '', email: '', company: '', message: '' });
  const [orgRequestLoading, setOrgRequestLoading] = useState(false);
  const [orgRequestSuccess, setOrgRequestSuccess] = useState(false);

  const handleOrgAccessRequest = async () => {
    if (!orgRequestData.name || !orgRequestData.email || !orgRequestData.company) {
      return;
    }
    
    setOrgRequestLoading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/auth/request-org-access`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(orgRequestData),
      });

      if (response.ok) {
        setOrgRequestSuccess(true);
        setOrgRequestData({ name: '', email: '', company: '', message: '' });
      } else {
        console.error('Failed to submit organization access request');
      }
    } catch (error) {
      console.error('Error submitting organization access request:', error);
    } finally {
      setOrgRequestLoading(false);
    }
  };

  return (
    <>
      <Box
        display="flex"
        flexDirection={{ xs: 'column', lg: 'row' }}
        gap={{ xs: 4, md: 6 }}
        alignItems="stretch"
        justifyContent="center"
        flexWrap="wrap"
      >
      {/* Free Tier */}
      <Box sx={{ 
        flex: 1, 
        maxWidth: { md: '400px' },
        p: 4, 
        borderRadius: 3, 
        backgroundColor: '#fafafa',
        border: '2px solid #e0e0e0',
        textAlign: 'center',
        position: 'relative',
        minHeight: '320px',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between'
      }}>
        <Box>
          <Typography 
            variant="h5" 
            sx={{ 
              fontWeight: 700, 
              mb: 1,
              color: '#2e7d32',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 1
            }}
          >
            <SchoolIcon sx={{ fontSize: '1.5rem' }} />
            Explore
          </Typography>
          <Typography 
            variant="h3" 
            sx={{ 
              fontWeight: 700, 
              color: '#2e7d32',
              mb: 2
            }}
          >
            Free
          </Typography>
          <Typography 
            variant="body2" 
            sx={{ 
              color: 'text.secondary', 
              fontSize: '0.875rem'
            }}
          >
            Create and try Decision Games today. Decision Moments for short practice are being introduced.
          </Typography>
        </Box>
        {showButtons && (
          <Button
            variant="outlined"
            size="large"
            onClick={onStartFree}
            disabled={hasSubscription || isAuthenticated}
            sx={{
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 600
            }}
          >
            Start exploring
          </Button>
        )}
      </Box>

      {/* Organizations */}
      <Box sx={{ 
        flex: 1, 
        maxWidth: { md: '400px' },
        p: 4, 
        borderRadius: 3, 
        backgroundColor: '#f3f6ff',
        border: '2px solid #1976d2',
        textAlign: 'center',
        position: 'relative',
        minHeight: '320px',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between'
      }}>
        <Box>
          <Typography 
            variant="h5" 
            sx={{ 
              fontWeight: 700, 
              mb: 1,
              color: '#1976d2',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 1
            }}
          >
            <BusinessIcon sx={{ fontSize: '1.5rem' }} />
            Organizations
          </Typography>
          <Typography 
            variant="h5" 
            sx={{ 
              fontWeight: 700, 
              color: '#1976d2', 
              mb: 2
            }}
          >
            Annual plans, quoted
          </Typography>
          <Typography 
            variant="body2" 
            sx={{ 
              color: 'text.secondary', 
              fontSize: '0.875rem'
            }}
          >
            Create and reuse Decision Games from business context and approved knowledge. Hosted sessions are included. Decision Moments are being introduced.
          </Typography>
        </Box>
        {showButtons && (
          <Button
            variant="contained"
            size="large"
            onClick={() => setOrgAccessModalOpen(true)}
            sx={{
              backgroundColor: '#1976d2',
              color: 'white',
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 600,
              '&:hover': {
                backgroundColor: '#1565c0'
              }
            }}
          >
            Discuss an organization pilot
          </Button>
        )}
      </Box>

      {/* Organization Plan */}
      <Box sx={{ 
        flex: 1, 
        maxWidth: { md: '400px' },
        p: 4, 
        borderRadius: 3, 
        backgroundColor: '#faf5f2',
        border: '2px solid #d84315',
        textAlign: 'center',
        position: 'relative',
        overflow: 'hidden',
        minHeight: '320px',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between'
      }}>
        
        <Box>
          <Typography 
            variant="h5" 
            sx={{ 
              fontWeight: 700, 
              mb: 1,
              color: '#d84315',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 1
            }}
          >
            <HandshakeIcon sx={{ fontSize: '1.5rem' }} />
            Expert partners
          </Typography>
          <Typography 
            variant="h5" 
            sx={{ 
              fontWeight: 700, 
              color: '#d84315',
              mb: 2
            }}
          >
            Custom agreement
          </Typography>
          <Typography 
            variant="body2" 
            sx={{ 
              color: 'text.secondary', 
              fontSize: '0.875rem'
            }}
          >
            Use your expertise to build Decision Games for client programs. Decision Moments are being introduced. Commercial rights and delivery scope are agreed with each partner.
          </Typography>
        </Box>
        {showButtons && (
          <Button
            variant="outlined"
            component="a"
            href="mailto:info@itclarifies.com?subject=Partnership"
            sx={{
              borderColor: '#d84315',
              color: '#d84315',
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 600,
              '&:hover': {
                borderColor: '#bf360c',
                backgroundColor: 'rgba(216, 67, 21, 0.04)'
              }
            }}
          >
            Discuss a partnership
          </Button>
        )}
      </Box>
      </Box>

      {/* Organization Access Request Modal */}
      <Dialog 
        open={orgAccessModalOpen} 
        onClose={() => {
          setOrgAccessModalOpen(false);
          setOrgRequestSuccess(false);
          setOrgRequestData({ name: '', email: '', company: '', message: '' });
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ textAlign: 'center' }}>
          Discuss an organization pilot
        </DialogTitle>
        <DialogContent>
          {orgRequestSuccess ? (
            <Box sx={{ textAlign: 'center', py: 3 }}>
              <Typography variant="h6" sx={{ color: 'success.main', mb: 2 }}>
                ✅ Request Submitted Successfully!
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                Thank you. We will be in touch about an organization pilot.
              </Typography>
            </Box>
          ) : (
            <>
              <Typography variant="body2" sx={{ mb: 3, color: 'text.secondary' }}>
                Tell us about the organization and the decision practice you want to run.
              </Typography>
              
              <TextField
                label="Your Name"
                fullWidth
                margin="dense"
                value={orgRequestData.name}
                onChange={(e) => setOrgRequestData({ ...orgRequestData, name: e.target.value })}
                required
              />
              
              <TextField
                label="Your Email"
                type="email"
                fullWidth
                margin="dense"
                value={orgRequestData.email}
                onChange={(e) => setOrgRequestData({ ...orgRequestData, email: e.target.value })}
                required
              />
              
              <TextField
                label="Company/Organization"
                fullWidth
                margin="dense"
                value={orgRequestData.company}
                onChange={(e) => setOrgRequestData({ ...orgRequestData, company: e.target.value })}
                required
              />
              
              <TextField
                label="Message (Optional)"
                fullWidth
                multiline
                minRows={2}
                maxRows={4}
                margin="dense"
                value={orgRequestData.message}
                onChange={(e) => setOrgRequestData({ ...orgRequestData, message: e.target.value })}
                placeholder="Tell us about your organization's needs..."
              />
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center', pb: 2 }}>
          {!orgRequestSuccess ? (
            <>
              <Button
                variant="contained"
                onClick={handleOrgAccessRequest}
                disabled={orgRequestLoading || !orgRequestData.name || !orgRequestData.email || !orgRequestData.company}
                sx={{ minWidth: 120 }}
              >
                {orgRequestLoading ? 'Submitting...' : 'Request Offer'}
              </Button>
              <Button
                variant="outlined"
                onClick={() => {
                  setOrgAccessModalOpen(false);
                  setOrgRequestData({ name: '', email: '', company: '', message: '' });
                }}
              >
                Cancel
              </Button>
            </>
          ) : (
            <Button
              variant="contained"
              onClick={() => setOrgAccessModalOpen(false)}
            >
              Close
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </>
  );
};

export default PricingCards;
